import { z } from 'zod';
import { escapeHtml } from '../../render/escape.js';
import { normalizeAnchor } from '../../schema/anchor.js';
import type { DefModuleMountT } from '../../schema/split.js';
import { ModuleOpError, identityMigration, type FirstPartyModule, type ModuleOperation } from '../kernel.js';
import {
  HoursException,
  HoursRule,
  VenueMenuHoursConfig,
  VenueMenuHoursData,
  type VenueMenuHoursConfigT,
  type VenueMenuHoursDataT,
  type WeekdayT,
} from './schema.js';

const id = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const name = z.string().min(1).max(160);
const categoryName = z.string().min(1).max(120);
const price = z.string().max(40);
const description = z.string().max(400);

const addCategoryInput = z.object({ name: categoryName }).strict();
const renameCategoryInput = z.object({ categoryId: id, name: categoryName }).strict();
const deleteCategoryInput = z.object({ categoryId: id }).strict();
const reorderCategoriesInput = z.object({ orderedIds: z.array(id) }).strict();
const addItemInput = z.object({
  categoryId: id,
  name,
  description: description.optional(),
  price,
}).strict();
const updateItemInput = z.object({
  categoryId: id,
  itemId: id,
  fields: z.object({
    name: name.optional(),
    description: description.optional(),
    price: price.optional(),
  }).strict().refine((fields) => Object.keys(fields).length > 0, 'at least one field is required'),
}).strict();
const deleteItemInput = z.object({ categoryId: id, itemId: id }).strict();
const reorderItemsInput = z.object({ categoryId: id, orderedIds: z.array(id) }).strict();
const setRegularHoursInput = z.object({ rules: z.array(HoursRule) }).strict();
const setExceptionsInput = z.object({ exceptions: z.array(HoursException) }).strict();

type Category = VenueMenuHoursDataT['menus'][number];
type Item = Category['items'][number];
type Locale = VenueMenuHoursConfigT['locale'];

const DEFAULT_CONFIG: VenueMenuHoursConfigT = { locale: 'cs' };
const WEEKDAY_LABELS: Record<Locale, Record<WeekdayT, string>> = {
  cs: { mon: 'Pondělí', tue: 'Úterý', wed: 'Středa', thu: 'Čtvrtek', fri: 'Pátek', sat: 'Sobota', sun: 'Neděle' },
  en: { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' },
  pl: { mon: 'Poniedziałek', tue: 'Wtorek', wed: 'Środa', thu: 'Czwartek', fri: 'Piątek', sat: 'Sobota', sun: 'Niedziela' },
};
const CLOSED_LABELS: Record<Locale, string> = { cs: 'Zavřeno', en: 'Closed', pl: 'Zamknięte' };
const EXCEPTIONS_LABELS: Record<Locale, string> = { cs: 'Výjimky', en: 'Exceptions', pl: 'Wyjątki' };

function invalidInput(error: z.ZodError): ModuleOpError {
  return new ModuleOpError('invalid-input', error.issues[0]?.message ?? 'invalid operation input');
}

function parseInput<T>(schema: z.ZodType<T>, input: unknown): T {
  const parsed = schema.safeParse(input);
  if (!parsed.success) throw invalidInput(parsed.error);
  return parsed.data;
}

function cloneData(data: VenueMenuHoursDataT): VenueMenuHoursDataT {
  return structuredClone(data);
}

function slugify(value: string): string {
  return normalizeAnchor(value) || 'item';
}

/** Max id length (matches the Slug schema); reserve room for a `-NN` suffix. */
const MAX_ID_LENGTH = 80;

function nextId(value: string, existingIds: Iterable<string>): string {
  const existing = new Set(existingIds);
  const base = slugify(value).slice(0, MAX_ID_LENGTH).replace(/-+$/, '') || 'item';
  if (!existing.has(base)) return base;
  let suffix = 2;
  for (;;) {
    // Trim the base so base + "-<suffix>" always fits MAX_ID_LENGTH.
    const trimmed = base.slice(0, MAX_ID_LENGTH - `-${suffix}`.length).replace(/-+$/, '') || 'item';
    const candidate = `${trimmed}-${suffix}`;
    if (!existing.has(candidate)) return candidate;
    suffix += 1;
  }
}

function findCategory(data: VenueMenuHoursDataT, categoryId: string): Category {
  const category = data.menus.find((candidate) => candidate.id === categoryId);
  if (!category) throw new ModuleOpError('category-not-found', `category "${categoryId}" was not found`);
  return category;
}

function findItem(category: Category, itemId: string): Item {
  const item = category.items.find((candidate) => candidate.id === itemId);
  if (!item) throw new ModuleOpError('item-not-found', `item "${itemId}" was not found`);
  return item;
}

function reorderByIds<T extends { id: string }>(items: T[], orderedIds: string[], label: string): T[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const uniqueIds = new Set(orderedIds);
  const hasEveryItem = items.every((item) => uniqueIds.has(item.id));
  if (orderedIds.length !== items.length || uniqueIds.size !== orderedIds.length || !hasEveryItem) {
    throw new ModuleOpError('invalid-order', `${label} order must contain every current id exactly once`);
  }
  return orderedIds.map((orderedId) => byId.get(orderedId)!);
}

function operation<T>(
  schema: z.ZodType<T>,
  describe: string,
  transform: (data: VenueMenuHoursDataT, input: T) => void,
): ModuleOperation<VenueMenuHoursDataT> {
  return {
    input: schema,
    describe,
    apply(data, input) {
      const parsed = parseInput(schema, input);
      const result = cloneData(data);
      transform(result, parsed);
      return result;
    },
  };
}

const operations: Record<string, ModuleOperation<VenueMenuHoursDataT>> = {
  addCategory: operation(addCategoryInput, 'Add a menu category.', (data, input) => {
    data.menus.push({ id: nextId(input.name, data.menus.map((category) => category.id)), name: input.name, items: [] });
  }),
  renameCategory: operation(renameCategoryInput, 'Rename a menu category.', (data, input) => {
    findCategory(data, input.categoryId).name = input.name;
  }),
  deleteCategory: operation(deleteCategoryInput, 'Delete a menu category.', (data, input) => {
    findCategory(data, input.categoryId);
    data.menus = data.menus.filter((category) => category.id !== input.categoryId);
  }),
  reorderCategories: operation(reorderCategoriesInput, 'Reorder all menu categories.', (data, input) => {
    data.menus = reorderByIds(data.menus, input.orderedIds, 'category');
  }),
  addItem: operation(addItemInput, 'Add an item to a menu category.', (data, input) => {
    const category = findCategory(data, input.categoryId);
    const item: Item = {
      id: nextId(input.name, category.items.map((candidate) => candidate.id)),
      name: input.name,
      price: input.price,
    };
    if (input.description !== undefined) item.description = input.description;
    category.items.push(item);
  }),
  updateItem: operation(updateItemInput, 'Update fields on a menu item.', (data, input) => {
    // Only sent keys are present (zod drops absent optionals), so assign patches exactly those.
    Object.assign(findItem(findCategory(data, input.categoryId), input.itemId), input.fields);
  }),
  deleteItem: operation(deleteItemInput, 'Delete a menu item.', (data, input) => {
    const category = findCategory(data, input.categoryId);
    findItem(category, input.itemId);
    category.items = category.items.filter((item) => item.id !== input.itemId);
  }),
  reorderItems: operation(reorderItemsInput, 'Reorder all items in a menu category.', (data, input) => {
    const category = findCategory(data, input.categoryId);
    category.items = reorderByIds(category.items, input.orderedIds, 'item');
  }),
  setRegularHours: operation(setRegularHoursInput, 'Replace regular opening-hour rules.', (data, input) => {
    data.hours.regular = structuredClone(input.rules);
  }),
  setExceptions: operation(setExceptionsInput, 'Replace dated opening-hour exceptions.', (data, input) => {
    data.hours.exceptions = structuredClone(input.exceptions);
  }),
};

function sectionOpen(mount: DefModuleMountT, view: string): string {
  return `<section id="${escapeHtml(mount.anchor)}" class="ve-module ve-venue-menu-hours ve-venue-menu-hours--${escapeHtml(view)}">`;
}

function renderMenuItem(item: Item): string {
  const lines = [
    '      <div class="ve-menu-card__item">',
    '        <div class="ve-menu-card__line">',
    `          <span class="ve-menu-card__name">${escapeHtml(item.name)}</span>`,
    '          <span class="ve-menu-card__leader" aria-hidden="true"></span>',
    `          <span class="ve-menu-card__price">${escapeHtml(item.price)}</span>`,
    '        </div>',
  ];
  if (item.description) lines.push(`        <p class="ve-menu-card__desc">${escapeHtml(item.description)}</p>`);
  lines.push('      </div>');
  return lines.join('\n');
}

function renderCategory(category: Category): string {
  const items = category.items.map(renderMenuItem).join('\n');
  return [
    '  <div class="ve-menu-card__category">',
    `    <h2 class="ve-menu-card__heading">${escapeHtml(category.name)}</h2>`,
    '    <div class="ve-menu-card__items">',
    items,
    '    </div>',
    '  </div>',
  ].join('\n');
}

function renderMenuCard(mount: DefModuleMountT, data: VenueMenuHoursDataT): { html: string } {
  const categories = data.menus.map(renderCategory);
  return { html: [sectionOpen(mount, 'menu-card'), ...categories, '</section>'].join('\n') };
}

function renderHoursRule(rule: VenueMenuHoursDataT['hours']['regular'][number], config: VenueMenuHoursConfigT): string {
  const days = rule.days.map((day) => WEEKDAY_LABELS[config.locale][day]).join(', ');
  const hours = 'closed' in rule ? CLOSED_LABELS[config.locale] : `${rule.open}–${rule.close}`;
  return [
    '  <div class="ve-hours__row">',
    `    <span class="ve-hours__day">${escapeHtml(days)}</span>`,
    `    <span class="ve-hours__time">${escapeHtml(hours)}</span>`,
    '  </div>',
  ].join('\n');
}

function renderException(exception: VenueMenuHoursDataT['hours']['exceptions'][number], config: VenueMenuHoursConfigT): string {
  const hours = 'closed' in exception ? CLOSED_LABELS[config.locale] : `${exception.open}–${exception.close}`;
  const note = exception.note ? ` <span class="ve-hours__note">${escapeHtml(exception.note)}</span>` : '';
  return [
    '  <div class="ve-hours__row ve-hours__row--exception">',
    `    <span class="ve-hours__day">${escapeHtml(exception.date)}${note}</span>`,
    `    <span class="ve-hours__time">${escapeHtml(hours)}</span>`,
    '  </div>',
  ].join('\n');
}

function renderHours(
  mount: DefModuleMountT,
  data: VenueMenuHoursDataT,
  config: VenueMenuHoursConfigT = DEFAULT_CONFIG,
): { html: string } {
  const parsedConfig = VenueMenuHoursConfig.parse(config);
  const regular = data.hours.regular.map((rule) => renderHoursRule(rule, parsedConfig));
  const exceptions = data.hours.exceptions.map((exception) => renderException(exception, parsedConfig));
  const parts = [sectionOpen(mount, 'hours'), '  <div class="ve-hours__rows">', ...regular, '  </div>'];
  if (exceptions.length > 0) {
    parts.push(`  <h2 class="ve-hours__exceptions-heading">${escapeHtml(EXCEPTIONS_LABELS[parsedConfig.locale])}</h2>`);
    parts.push('  <div class="ve-hours__exceptions">', ...exceptions, '  </div>');
  }
  parts.push('</section>');
  return { html: parts.join('\n') };
}

const migrate = identityMigration('venue.menu-hours', VenueMenuHoursData);

export const venueMenuHours: FirstPartyModule<VenueMenuHoursDataT, VenueMenuHoursConfigT> = {
  id: 'venue.menu-hours',
  contractVersion: 1,
  dataVersion: 1,
  dataSchema: VenueMenuHoursData,
  configSchema: VenueMenuHoursConfig,
  operations,
  views: {
    'menu-card': { render: renderMenuCard },
    hours: { render: renderHours },
  },
  migrate,
};
